import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import TextInput from '@narvar/nth-text-input';
import Button, { outline } from '@narvar/nth-button';
import { Container, Row } from '@narvar/nth-grid';
import Icon from '../Icon';
import Loader from '../Loader';
import {
  QTY_FIELD_ACTION_MODES,
  QTY_INPUT,
  QTY_WITH_EXPIRY_INPUT,
} from '../../constants/fieldTypes';

const DeleteContainer = styled.div`
  :hover {
    cursor: pointer;
    path {
      fill: #2c3239;
    }
  }
  position: absolute;
  top: 0.9rem;
  right: 0.8rem;
`;

export const mapStateToProps = ({
  language,
  page: { languageCode } = {},
  retailerSettings: { branding: { brand_call_to_action: brandCTAColor } = {} } = {},
}) => ({
  language,
  languageCode,
  brandCTAColor,
});

const QuantityField = ({
  type,
  validate,
  returnQuantity,
  handleOnQtyChange,
  handleVerifyQtyField,
  handleExpiryGroupChange,
  language,
  expiryList,
  brandCTAColor,
  quantityFieldAction,
  isVerifying,
  handleAddExpiryGroup,
  removeGroup,
  error,
}) => {
  const renderElement = () => {
    switch (type) {
      case QTY_INPUT:
        return (
          <TextInput
            type="number"
            id="qty"
            name="qty"
            label={language[`returns.step_1.sidepanel_quantity_field.${type}.qty.placeholder`]}
            onChange={handleOnQtyChange}
            value={returnQuantity || null}
            defaultValue={returnQuantity || null}
          />
        );

      case QTY_WITH_EXPIRY_INPUT:
        return (
          <div>
            <div style={{ margin: '1rem 0', fontSize: '0.9rem' }}>
              {language[`returns.step_1.sidepanel_quantity_field.${type}.subtitle`]}
            </div>
            {expiryList.map(({ values: { qty, expirationDate } = {}, valid, key }, index) => (
              <div
                key={key}
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                }}
                data-test={`expiry-group-${index}`}
              >
                <TextInput
                  s={{
                    width: '32%',
                    ...(error.field === 'qty' && !valid ? { borderColor: 'red' } : {}),
                    ...(valid ? { backgroundColor: '#F5F5F5' } : {}),
                  }}
                  id={`qty-${key}`}
                  name="qty"
                  type="number"
                  label={
                    language[`returns.step_1.sidepanel_quantity_field.${type}.qty.placeholder`]
                  }
                  onChange={({ target: { name, value } }) =>
                    handleExpiryGroupChange(name, parseInt(value, 10))
                  }
                  defaultValue={qty || null}
                  value={qty || null}
                  disabled={valid}
                  labelStyle={
                    valid
                      ? { top: '0.4rem', transform: 'none', fontSize: '0.75rem', color: '#a0a0a0' }
                      : {}
                  }
                />
                <TextInput
                  s={{
                    width: '65%',
                    ...(error.field === 'expirationDate' && !valid ? { borderColor: 'red' } : {}),
                    ...(valid ? { backgroundColor: '#F5F5F5' } : {}),
                  }}
                  id={`expirationDate-${key}`}
                  name="expirationDate"
                  label={
                    language[`returns.step_1.sidepanel_quantity_field.${type}.expiry.placeholder`]
                  }
                  onChange={({ target: { name, value } }) => handleExpiryGroupChange(name, value)}
                  defaultValue={expirationDate || null}
                  value={expirationDate || null}
                  maxLength={10}
                  disabled={valid}
                  labelStyle={
                    valid
                      ? { top: '0.4rem', transform: 'none', fontSize: '0.75rem', color: '#a0a0a0' }
                      : {}
                  }
                />

                <div
                  style={{
                    position: 'absolute',
                    top: '0.5rem',
                    right: '2.5rem',
                  }}
                >
                  {valid && <Icon.SuccessCheckmark borderless fill="#2ECC71" width="2rem" />}
                </div>
                <DeleteContainer onClick={() => removeGroup(index)}>
                  <Icon.Delete width="1rem" />
                </DeleteContainer>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const renderAction = () => {
    if (isVerifying) {
      return (
        <Row style={{ marginTop: '1.6rem' }}>
          <Loader />
        </Row>
      );
    }

    switch (quantityFieldAction) {
      case QTY_FIELD_ACTION_MODES.DISABLE_VERIFY:
      case QTY_FIELD_ACTION_MODES.ENABLE_VERIFY: {
        const enableVerify = quantityFieldAction === QTY_FIELD_ACTION_MODES.ENABLE_VERIFY;
        return (
          <Row style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            <Button
              big
              disabled={!enableVerify}
              onClick={handleVerifyQtyField}
              s={{
                ...outline({ disabled: !enableVerify, active: enableVerify }),
                ...(enableVerify
                  ? {
                      borderColor: brandCTAColor,
                      color: brandCTAColor,
                    }
                  : {
                      borderColor: '#cccccc',
                      color: '#c3c3c3',
                    }),
              }}
            >
              {language[`returns.step_1.sidepanel_quantity_field.${type}.validate`]}
            </Button>
          </Row>
        );
      }

      case QTY_FIELD_ACTION_MODES.ENABLE_ADD: {
        return (
          <Row style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            <Button className="narvar-btn-tertiary" onClick={handleAddExpiryGroup}>
              + Add another quantity
            </Button>
          </Row>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Container>
      <Row>{renderElement()}</Row>
      {error.message && (
        <div
          style={{
            color: '#E53935',
            fontSize: '0.9rem',
            fontWeight: 700,
            padding: '0 1.5rem',
            marginTop: '0.8rem',
            marginBottom: '1rem',
          }}
        >
          {error.message}
        </div>
      )}
      {validate && renderAction()}
    </Container>
  );
};

QuantityField.propTypes = {
  type: PropTypes.string.isRequired,
  enumerable: PropTypes.bool.isRequired,
  validate: PropTypes.bool.isRequired,
  returnQuantity: PropTypes.number.isRequired,
  handleOnQtyChange: PropTypes.func.isRequired,
  handleVerifyQtyField: PropTypes.func,
  handleExpiryGroupChange: PropTypes.func,
  language: PropTypes.object.isRequired,
  expiryList: PropTypes.array,
  brandCTAColor: PropTypes.string,
  quantityFieldAction: PropTypes.string,
  isVerifying: PropTypes.bool,
  handleAddExpiryGroup: PropTypes.func,
  removeGroup: PropTypes.func,
  error: PropTypes.shape({
    field: PropTypes.string,
    mmessage: PropTypes.string,
  }),
};

QuantityField.defaultProps = {
  handleVerifyQtyField: undefined,
  handleExpiryGroupChange: undefined,
  expiryList: [],
  brandCTAColor: '#000000',
  quantityFieldAction: undefined,
  isVerifying: false,
  handleAddExpiryGroup: undefined,
  removeGroup: undefined,
  error: undefined,
};

export default connect(mapStateToProps)(QuantityField);
